import { request } from '@/lib/api'

/**
 * «Следующий шаг» по сделке (тикет #133): рекомендации, найденные правилами, с поводом от ИИ.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе.
 */

export type RecommendationKind = 'quote_unanswered' | 'stage_stuck' | 'no_activity'

export interface RecommendationReason {
  /** `ai` — написал ИИ, `template` — шаблон системы. */
  source: 'ai' | 'template'
  /** `ready`, `pending` (ИИ пишет), `template`, `unavailable` (ИИ выключен), `failed` (ИИ не справился). */
  state: 'ready' | 'pending' | 'template' | 'unavailable' | 'failed'
  text: string
  opener?: string | null
  /** Почему повод — шаблон, словами сервера. */
  note?: string | null
}

export interface Recommendation {
  id: string
  dealId: string
  dealNumber: string
  dealTitle: string
  counterpartyName?: string | null
  responsibleEmployeeId?: string | null
  responsibleName?: string | null
  kind: RecommendationKind
  subjectType: 'quote' | 'stage' | 'deal'
  subjectId: string
  subjectAt: string
  days: number
  /** Что увидело правило — словами. */
  situation: string
  /** Тема задачи, которую создаст «Создать задачу». */
  action: string
  reason: RecommendationReason
  status: 'open' | 'accepted' | 'dismissed' | 'resolved'
  taskId?: string | null
  decidedAt?: string | null
  decisionNote?: string | null
  createdAt: string
  /** Сделка в области `sales.deal:update` смотрящего — можно отклонить. */
  mayDecide: boolean
  /** …и есть `sales.activity:create` — можно создать задачу. */
  mayAccept: boolean
}

export interface DealRecommendations {
  recommendations: Recommendation[]
  history: Recommendation[]
}

export interface MyRecommendations {
  hasEmployeeCard: boolean
  recommendations: Recommendation[]
}

export interface AcceptResult {
  taskId: string
  created: boolean
  recommendation: Recommendation
}

export function fetchDealRecommendations(dealId: string, signal?: AbortSignal): Promise<DealRecommendations> {
  return request<DealRecommendations>(`/sales/deals/${dealId}/recommendations`, { signal })
}

export function fetchMyRecommendations(signal?: AbortSignal): Promise<MyRecommendations> {
  return request<MyRecommendations>('/sales/recommendations/mine', { signal })
}

export function acceptRecommendation(id: string): Promise<AcceptResult> {
  return request<AcceptResult>(`/sales/recommendations/${id}/accept`, { method: 'POST', body: {} })
}

export function dismissRecommendation(id: string, reason?: string): Promise<Recommendation> {
  return request<Recommendation>(`/sales/recommendations/${id}/dismiss`, {
    method: 'POST',
    body: reason != null && reason.trim() !== '' ? { reason: reason.trim() } : {},
  })
}

export function fetchNextStepSwitch(signal?: AbortSignal): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>('/admin/integrations/ai-next-step', { signal })
}

export function saveNextStepSwitch(enabled: boolean): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>('/admin/integrations/ai-next-step', { method: 'PUT', body: { enabled } })
}
